import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import SeoHelmet from '../components/SeoHelmet';
import { breadcrumbSchema } from '../utils/schemaOrg';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Documents = () => {
  const { currentLanguage, t } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadData();
  }, [currentLanguage]);

  const loadData = async () => {
    try {
      const [docRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/documents?language=${currentLanguage}`),
        axios.get(`${API_URL}/api/document-categories?language=${currentLanguage}`),
      ]);
      setDocuments(docRes.data || []);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  // Map category key -> localized name
  const catNameByKey = categories.reduce((acc, c) => { acc[c.key] = c.name; return acc; }, {});
  const catLabel = (key) => catNameByKey[key] || key;

  // Only show category chips that actually have documents
  const usedCategoryKeys = [...new Set(documents.map((d) => d.category))];
  const visibleCategories = categories.filter((c) => usedCategoryKeys.includes(c.key));

  const filteredDocuments = selectedCategory === 'all'
    ? documents
    : documents.filter(d => d.category === selectedCategory);

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-xl text-gray-600">{t('loading') || 'Загрузка...'}</div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SeoHelmet
        title={currentLanguage === 'EN' ? 'Regulatory Documents | VELOX HINT' : 'Нормативные документы | ВЕЛОКС ХИНТ'}
        description={currentLanguage === 'EN' ? 'Current technical regulations, laws and regulatory acts for foreign trade and certification.' : 'Актуальные технические регламенты, законы и нормативные акты для ВЭД и сертификации.'}
        jsonLd={breadcrumbSchema([
          { name: currentLanguage === 'EN' ? 'Home' : 'Главная', path: '/' },
          { name: currentLanguage === 'EN' ? 'Documents' : 'Документы', path: '/documents' },
        ])}
      />
      <Header />
      <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {currentLanguage === 'RU' ? 'Нормативные документы' : 'Regulatory Documents'}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentLanguage === 'RU' 
              ? 'Актуальные технические регламенты, законы и нормативные акты'
              : 'Current technical regulations, laws and regulatory acts'}
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8 flex flex-wrap gap-3 justify-center" data-testid="documents-category-filter">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-6 py-2 rounded-lg font-medium transition ${
              selectedCategory === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            {currentLanguage === 'RU' ? 'Все' : 'All'}
          </button>
          {visibleCategories.map((category) => (
            <button
              key={category.key}
              onClick={() => setSelectedCategory(category.key)}
              className={`px-6 py-2 rounded-lg font-medium transition ${
                selectedCategory === category.key
                  ? 'bg-orange-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((doc) => (
            <div
              key={doc.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition p-6"
            >
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full">
                  {catLabel(doc.category)}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{doc.title}</h3>
              <p className="text-gray-600 mb-4 line-clamp-3">{doc.description}</p>
              <a
                href={doc.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-orange-500 hover:text-orange-600 font-medium"
              >
                {currentLanguage === 'RU' ? 'Открыть документ' : 'Open document'}
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          ))}
        </div>

        {filteredDocuments.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {currentLanguage === 'RU' ? 'Документы не найдены' : 'No documents found'}
            </p>
          </div>
        )}
      </div>
    </div>
    <Contact />
    <Footer />
    </>
  );
};

export default Documents;